import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus,
  RefreshCw,
  Search,
  Settings,
  Trash2,
  Lock,
  Unlock,
  Pencil,
  BookOpen,
  Activity,
  X,
  ChevronRight,
} from "lucide-react";

import {
  useAdminGetExamsQuery,
  useAdminCreateExamMutation,
  useAdminUpdateExamMutation,
  useAdminDeleteExamMutation,
} from "../../api/examsApi";

// Security & UI Patterns
import PageGate from "../../../../components/PageGate";
import AccessGate from "../../../../components/AccessGate";
import { PERMS } from "../../../../utils/perms";

function safeMsg(err: any) {
  return (
    err?.data?.message ||
    err?.data?.error ||
    err?.error ||
    err?.message ||
    "Something went wrong"
  );
}

/** * Hover-Triggered Tooltip */
function Tooltip({
  children,
  text,
}: {
  children: React.ReactNode;
  text: string;
}) {
  return (
    <div className="relative group flex items-center justify-center">
      {children}
      <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 hidden group-hover:block z-[60] pointer-events-none animate-in fade-in zoom-in-95 duration-100">
        <div className="bg-gray-900 text-white text-[9px] font-black uppercase tracking-[0.15em] px-2 py-1.5 rounded shadow-xl whitespace-nowrap border border-gray-700">
          {text}
        </div>
        <div className="w-2 h-2 bg-gray-900 rotate-45 mx-auto -mt-1 border-r border-b border-gray-700"></div>
      </div>
    </div>
  );
}

/**
 * An exam that is an ALTERNATIVE WAY IN to the one being edited.
 *
 * There are two such routes, and this renders either:
 *
 *   "pass" — the candidate must have PASSED that exam.
 *   "paid" — the candidate need only have a PAID application for it. This is
 *            the route for exams that run concurrently: an interview held
 *            beside the written exam it accompanies cannot ask for a pass in
 *            that exam, because nobody sitting it this diet has one yet.
 *
 * Neither is an extra hurdle. A candidate needs the logbook stage OR one of
 * these, whichever they have — interviews sit at the end of more than one path,
 * and someone who came through TPC has not failed by lacking a logbook. The
 * note under the field says so in as many words, because "requires" almost
 * always reads as "as well as".
 */
function AlternateRouteField({
  kind, exams, value, stage, otherRouteSet, excludeId, onChange,
}: {
  /** Which route this field sets — a pass in the exam, or a paid application. */
  kind: "pass" | "paid";
  exams: any[];
  value?: string | null;
  /** The logbook stage set alongside, so the note can name the real choice. */
  stage?: string;
  /** Is the OTHER exam route also set? Then the note has to say "any of". */
  otherRouteSet?: boolean;
  /** The exam being edited; it can never be its own way in. */
  excludeId?: string;
  onChange: (v: string | null) => void;
}) {
  const options = (exams ?? []).filter((x: any) => x.id !== excludeId);
  const stageSet = !!stage && stage !== "none";
  const stageLabel = stage ? `Logbook ${stage.replace("stage", "Stage ")}` : "";

  const copy =
    kind === "pass"
      ? {
          label: "Or a pass in",
          empty: "No exam prerequisite",
          alone: "Passing this exam qualifies a candidate.",
          blank: "Leave blank if this is not a way in.",
          withStage: (s: string) =>
            `Either ${s} or a pass in this exam qualifies a candidate — they do not need both.`,
        }
      : {
          label: "Or a paid application for",
          empty: "No paid-application route",
          alone:
            "Paying for this exam qualifies a candidate — they do not have to have passed it yet.",
          blank:
            "Leave blank unless a settled application for another exam is a way in.",
          withStage: (s: string) =>
            `Either ${s} or a paid application for this exam qualifies a candidate — they do not need both.`,
        };

  return (
    <div className="space-y-1">
      <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest">
        {copy.label}
      </label>
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value || null)}
        className="w-full border-b-2 border-gray-100 py-2 text-sm font-bold text-gray-800 bg-transparent outline-none focus:border-gray-900"
      >
        <option value="">{copy.empty}</option>
        {options.map((x: any) => (
          <option key={x.id} value={x.id}>
            {x.code ? `${x.code} — ${x.title}` : x.title}
          </option>
        ))}
      </select>
      <p className="text-[10px] text-gray-400">
        {!value
          ? copy.blank
          : otherRouteSet
            ? "Any ONE of the routes set here qualifies a candidate — they never need all of them."
            : stageSet
              ? copy.withStage(stageLabel)
              : copy.alone}
      </p>
    </div>
  );
}

/**
 * The entry bars an exam sets: CPD points, age, and years since graduating.
 *
 * Every one is optional and blank means "this exam does not ask" — which is not
 * the same as a bar of zero, so a cleared field is sent as null rather than 0.
 * Where each is enforced is stated on the field, because an admin setting a
 * minimum age needs to know it is judged against the member's record at APPLY
 * time, while years since graduation is judged against what the candidate
 * declares at submission.
 */
function EntryRequirementFields({
  values, onChange,
}: {
  values: any;
  onChange: (patch: Record<string, any>) => void;
}) {
  // "" clears the bar; anything else is a number. Sending 0 for a cleared box
  // would turn "does not ask" into "asks for zero", which reads the same on
  // screen and behaves differently in every gate.
  const set = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    onChange({ [key]: e.target.value === "" ? null : Number(e.target.value) });

  const box =
    "w-full bg-transparent border-b-2 border-gray-100 pb-2 text-sm font-bold text-gray-900 outline-none focus:border-gray-900 transition-colors";
  const cap =
    "block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1";

  return (
    <div className="space-y-4">
      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
        Entry requirements — leave blank if not applicable
      </p>

      {/* One column on a narrow screen: two number fields side by side, each
          with a caption and a note under it, is what pushed this off the edge
          on a phone. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={cap}>Minimum CPD</label>
          <input
            type="number" min={0} step="0.5" placeholder="e.g. 60"
            value={values.min_cpd_points ?? ""}
            onChange={set("min_cpd_points")}
            className={box}
          />
          <p className="mt-1 text-[10px] text-gray-400">Checked at apply and at submission.</p>
        </div>
        <div>
          <label className={cap}>Min years since graduation</label>
          <input
            type="number" min={0} placeholder="e.g. 5"
            value={values.min_years_since_graduation ?? ""}
            onChange={set("min_years_since_graduation")}
            className={box}
          />
          <p className="mt-1 text-[10px] text-gray-400">Candidate declares the year; blocks submission.</p>
        </div>
        <div>
          <label className={cap}>Minimum age</label>
          <input
            type="number" min={16} max={120} placeholder="e.g. 35"
            value={values.min_age ?? ""}
            onChange={set("min_age")}
            className={box}
          />
          <p className="mt-1 text-[10px] text-gray-400">From the member record, at apply.</p>
        </div>
        <div>
          <label className={cap}>Maximum age</label>
          <input
            type="number" min={16} max={120} placeholder="e.g. 60"
            value={values.max_age ?? ""}
            onChange={set("max_age")}
            className={box}
          />
          <p className="mt-1 text-[10px] text-gray-400">From the member record, at apply.</p>
        </div>
      </div>

      <div className="pt-4 border-t border-gray-100 space-y-3">
        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
          Documents the candidate uploads after paying
        </p>

        {DOCUMENT_FLAGS.map((doc) => (
          <label key={doc.key} className="flex items-start gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={!!values[doc.key]}
              onChange={(e) => onChange({ [doc.key]: e.target.checked })}
              className="shrink-0 w-4 h-4 mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span>
              <span className="block text-[10px] font-black text-gray-500 uppercase tracking-widest group-hover:text-gray-900 transition-colors">
                {doc.label}
              </span>
              {doc.hint ? (
                <span className="block text-[10px] text-gray-400 mt-0.5">{doc.hint}</span>
              ) : null}
            </span>
          </label>
        ))}

        <label className="flex items-start gap-3 cursor-pointer group pt-2 border-t border-gray-50">
          <input
            type="checkbox"
            checked={!!values.requires_course_selection}
            onChange={(e) => onChange({ requires_course_selection: e.target.checked })}
            className="shrink-0 w-4 h-4 mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          <span>
            <span className="block text-[10px] font-black text-gray-500 uppercase tracking-widest group-hover:text-gray-900 transition-colors">
              Candidate registers courses
            </span>
            <span className="block text-[10px] text-gray-400 mt-0.5">
              After paying, they pick from this exam's courses and must choose at least one.
            </span>
          </span>
        </label>

        {/* Not a document, but the same kind of decision — what this exam does
            and does not ask of a candidate. Inverted on screen because "this
            exam has no sitting" is the exceptional case worth ticking. */}
        <label className="flex items-start gap-3 cursor-pointer group pt-2 border-t border-gray-50">
          <input
            type="checkbox"
            checked={values.requires_exam_centre === false}
            onChange={(e) => onChange({ requires_exam_centre: !e.target.checked })}
            className="shrink-0 w-4 h-4 mt-0.5 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
          />
          <span>
            <span className="block text-[10px] font-black text-gray-500 uppercase tracking-widest group-hover:text-gray-900 transition-colors">
              No sitting — decided on documents alone
            </span>
            <span className="block text-[10px] text-gray-400 mt-0.5">
              No paper, no interview. Candidates are not asked to pick an exam centre.
            </span>
          </span>
        </label>
      </div>
    </div>
  );
}

/**
 * The documents an exam may ask for, in the order they are asked.
 *
 * Mirrors Exam::DOCUMENTS on the API plus the two that are records rather than
 * single files: O'level (one row per subject) and academic qualifications
 * (institution, qualification, year and the certificate together).
 */
const DOCUMENT_FLAGS = [
  {
    key: "requires_olevel",
    label: "O'level results",
    hint: "Five credits. Pre-filled from their Institute record.",
  },
  {
    key: "requires_qualifications",
    // The Institute says "degree certificate" for this; on an exam that IS the
    // certificate attached to an academic qualification, so it is one tick, not
    // two, and the label carries both names.
    label: "Academic qualifications / degree certificate",
    hint: "Institution, qualification, year and the certificate. Shared with their Institute record.",
  },
  {
    key: "requires_workshop_certificate",
    label: "Workshop certificate",
    hint: "",
  },
  { key: "requires_technical_report", label: "Technical report", hint: "" },
  { key: "requires_attestation_letter", label: "Attestation letter", hint: "" },
] as const;

/**
 * A brand-new exam, before the admin touches anything.
 *
 * Defined once and spread on both the initial state and the post-create reset,
 * so the two cannot drift — the reset used to be a hand-copied duplicate, which
 * is exactly how a newly added field ends up sticking around from the last exam
 * the admin created.
 *
 * `requires_exam_centre` starts TRUE: an exam is a sitting unless it says
 * otherwise, which is what every exam in the catalogue is today.
 */
const BLANK_EXAM = {
  code: "",
  title: "",
  requires_logbook_stage: "none",
  // A second route in, alongside the stage above — either qualifies.
  prerequisite_exam_id: null as string | null,
  // A third: a settled application for that exam, no pass needed. For exams
  // that run at the same time as this one.
  paid_application_exam_id: null as string | null,
  is_active: true,
  is_interview: false,
  requires_application_form: false,
  // null, not 0 — a blank bar is silence, and nothing is checked.
  min_cpd_points: null as number | null,
  min_age: null as number | null,
  max_age: null as number | null,
  min_years_since_graduation: null as number | null,
  requires_exam_centre: true,
  requires_course_selection: false,
  requires_olevel: false,
  requires_qualifications: false,
  requires_workshop_certificate: false,
  requires_technical_report: false,
  requires_attestation_letter: false,
};

export default function AdminExamsPage() {
  const {
    data = [],
    isLoading,
    error,
    refetch,
    isFetching,
  } = useAdminGetExamsQuery();
  const [createExam, { isLoading: creating }] = useAdminCreateExamMutation();
  const [updateExam, { isLoading: updating }] = useAdminUpdateExamMutation();
  const [deleteExam, { isLoading: deleting }] = useAdminDeleteExamMutation();

  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<any | null>(null);
  const [msg, setMsg] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  const [form, setForm] = useState({ ...BLANK_EXAM });

  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    return data.filter((x: any) =>
      `${x.title ?? ""} ${x.code ?? ""}`.toLowerCase().includes(s)
    );
  }, [data, q]);

  const stats = useMemo(
    () => ({
      total: data.length,
      active: data.filter((x: any) => x.is_active).length,
    }),
    [data]
  );

  const onCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg(null);
    try {
      await createExam(form as any).unwrap();
      setMsg({
        type: "success",
        text: "Exam template successfully registered",
      });
      setForm({ ...BLANK_EXAM });
    } catch (err) {
      setMsg({ type: "error", text: safeMsg(err) });
    }
  };

  const handleUpdate = async (id: string, body: any) => {
    setBusyId(id);
    try {
      await updateExam({ id, body }).unwrap();
      setMsg({ type: "success", text: "Catalog synchronized" });
      setEditTarget(null);
    } catch (err) {
      setMsg({ type: "error", text: safeMsg(err) });
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (x: any) => {
    if (!window.confirm(`PERMANENT ACTION: Delete exam "${x.title}"?`)) return;
    setBusyId(x.id);
    try {
      await deleteExam(x.id).unwrap();
      setMsg({ type: "success", text: "Exam purged from catalog" });
    } catch (err) {
      setMsg({ type: "error", text: safeMsg(err) });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <PageGate perm={PERMS.exam.access}>
      <div className="space-y-6 max-w-7xl mx-auto p-4 md:p-6 pb-20">
        {/* 1. Stats Bar */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            {
              label: "Exam Catalog",
              val: stats.total,
              icon: BookOpen,
              color: "indigo",
            },
            {
              label: "Active Items",
              val: stats.active,
              icon: Activity,
              color: "emerald",
            },
          ].map((s, i) => (
            <div
              key={i}
              className="bg-white border border-gray-100 p-6 rounded-3xl shadow-sm flex items-center gap-4"
            >
              <div
                className={`w-12 h-12 rounded-2xl flex items-center justify-center bg-${s.color}-50 text-${s.color}-600`}
              >
                <s.icon className="w-6 h-6" />
              </div>
              <div>
                <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">
                  {s.label}
                </div>
                <div className="text-xl font-black text-gray-900 tracking-tighter">
                  {s.val}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 2. Header */}
        <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-6">
              <div className="w-16 h-16 rounded-2xl bg-gray-900 flex items-center justify-center text-white shadow-xl">
                <Settings className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-3xl font-black text-gray-900 tracking-tight uppercase">
                  Exam Master
                </h1>
                <p className="text-gray-400 text-sm mt-2 font-medium">
                  Definition of global exam templates and requirements.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {["Diets", "Centers", "Grading"].map((link) => (
                <Link
                  key={link}
                  to={`/exams/admin/exam-${link.toLowerCase()}`}
                  className="px-4 py-2 rounded-xl border bg-white hover:bg-gray-50 text-[10px] font-black uppercase tracking-widest transition-all"
                >
                  {link}
                </Link>
              ))}
              <Tooltip text="Sync Catalog">
                <button
                  onClick={() => refetch()}
                  className="p-3 rounded-xl border bg-white hover:bg-gray-50"
                >
                  <RefreshCw
                    className={`w-4 h-4 text-indigo-500 ${
                      isFetching && "animate-spin"
                    }`}
                  />
                </button>
              </Tooltip>
            </div>
          </div>

          {msg && (
            <div
              className={`mt-6 p-4 rounded-2xl border flex justify-between items-center ${
                msg.type === "success"
                  ? "bg-emerald-50 border-emerald-100 text-emerald-800"
                  : "bg-rose-50 border-rose-100 text-rose-800"
              }`}
            >
              <span className="text-[10px] font-black uppercase tracking-widest">
                {msg.text}
              </span>
              <button onClick={() => setMsg(null)}>
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* 3. Create Sidebar */}
          <AccessGate perm={PERMS.exam.create}>
            <div className="lg:col-span-1">
              {/* Sticky only where there is a column beside it to stay level
                  with; on a narrow screen it simply flows. Capped and scrollable
                  either way — the form is now long enough that a fixed sticky
                  panel would put its own Register button below the fold with no
                  way to reach it. */}
              <div className="bg-white border border-gray-100 rounded-3xl p-5 sm:p-6 shadow-sm lg:sticky lg:top-6 lg:max-h-[calc(100vh-3rem)] lg:overflow-y-auto">
                <div className="flex items-center gap-2 mb-6 border-b pb-4">
                  <Plus className="w-4 h-4 text-indigo-500" />
                  <div className="font-black text-gray-900 text-[10px] uppercase tracking-widest">
                    New Template
                  </div>
                </div>

                <form onSubmit={onCreate} className="space-y-5">
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                      Internal Code
                    </label>
                    <input
                      placeholder="e.g. CORE-SPEC-01"
                      value={form.code}
                      onChange={(e) =>
                        setForm({ ...form, code: e.target.value })
                      }
                      className="w-full border-b-2 border-gray-100 py-2 font-black text-gray-800 outline-none focus:border-indigo-500 bg-transparent"
                      required
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                      Public Title
                    </label>
                    <input
                      placeholder="Exam Name"
                      value={form.title}
                      onChange={(e) =>
                        setForm({ ...form, title: e.target.value })
                      }
                      className="w-full border-b-2 border-gray-100 py-2 font-black text-gray-800 outline-none focus:border-indigo-500 bg-transparent"
                      required
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                      Eligibility Baseline
                    </label>
                    <select
                      value={form.requires_logbook_stage}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          requires_logbook_stage: e.target.value,
                        })
                      }
                      className="w-full border-b-2 border-gray-100 py-2 text-sm font-bold text-gray-800 bg-transparent outline-none"
                    >
                      <option value="none">No Logbook Required</option>
                      <option value="stage1">Logbook Stage 1</option>
                      <option value="stage2">Logbook Stage 2</option>
                      <option value="stage3">Logbook Stage 3</option>
                    </select>
                  </div>

                  <AlternateRouteField
                    kind="pass"
                    exams={data}
                    value={form.prerequisite_exam_id}
                    stage={form.requires_logbook_stage}
                    otherRouteSet={!!form.paid_application_exam_id}
                    onChange={(v) => setForm({ ...form, prerequisite_exam_id: v })}
                  />

                  <AlternateRouteField
                    kind="paid"
                    exams={data}
                    value={form.paid_application_exam_id}
                    stage={form.requires_logbook_stage}
                    otherRouteSet={!!form.prerequisite_exam_id}
                    onChange={(v) =>
                      setForm({ ...form, paid_application_exam_id: v })
                    }
                  />

                  <div className="flex flex-col gap-3 pt-2">
                    <label className="flex items-center gap-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={form.is_active}
                        onChange={(e) =>
                          setForm({ ...form, is_active: e.target.checked })
                        }
                        className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                      />
                      <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest group-hover:text-gray-900 transition-colors">
                        Catalog Active
                      </span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={form.is_interview}
                        onChange={(e) =>
                          setForm({ ...form, is_interview: e.target.checked })
                        }
                        className="w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                      />
                      <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest group-hover:text-gray-900 transition-colors">
                        Interview only (no courses)
                      </span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={form.requires_application_form}
                        onChange={(e) =>
                          setForm({ ...form, requires_application_form: e.target.checked })
                        }
                        className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest group-hover:text-gray-900 transition-colors">
                        Requires application form
                      </span>
                    </label>
                  </div>

                  <div className="pt-2 border-t border-gray-100">
                    <EntryRequirementFields
                      values={form}
                      onChange={(patch) => setForm({ ...form, ...patch })}
                    />
                  </div>

                  <Tooltip text="Register Exam">
                    <button
                      disabled={creating}
                      className="w-full bg-gray-900 text-white rounded-2xl py-4 text-[10px] font-black uppercase tracking-[0.2em] hover:bg-indigo-600 transition-all disabled:opacity-30 active:scale-95"
                    >
                      {creating ? "Syncing..." : "Initialize Exam"}
                    </button>
                  </Tooltip>
                </form>
              </div>
            </div>
          </AccessGate>

          {/* 4. Table List */}
          <div className="lg:col-span-2">
            <div className="bg-white border border-gray-100 rounded-[2.5rem] shadow-sm overflow-hidden">
              <div className="p-4 bg-gray-50/50 border-b flex items-center gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Filter catalog..."
                    className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-xs font-bold focus:ring-4 ring-indigo-50 outline-none"
                  />
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left bg-white border-b">
                      <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
                        Exam Spec
                      </th>
                      <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
                        Requirement
                      </th>
                      <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] text-center">
                        Status
                      </th>
                      <th className="px-8 py-5 text-right text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {rows.map((x: any) => (
                      <tr
                        key={x.id}
                        className="group hover:bg-gray-50/80 transition-all"
                      >
                        <td className="px-8 py-5">
                          <Link
                            to={`/exams/admin/exams/${x.id}`}
                            className="font-black text-gray-900 uppercase tracking-tight group-hover:text-indigo-600 flex items-center gap-2"
                          >
                            {x.title}{" "}
                            <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-all" />
                          </Link>
                          <div className="text-[10px] font-bold text-gray-400 mt-1 uppercase tracking-widest">
                            CODE: {x.code}
                            {x.is_interview && (
                              <span className="ml-2 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest border bg-amber-50 text-amber-700 border-amber-200">
                                Interview only
                              </span>
                            )}
                            {x.requires_application_form && (
                              <span className="ml-2 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest border bg-indigo-50 text-indigo-700 border-indigo-200">
                                Application form
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-8 py-5">
                          <div className="text-[10px] font-black text-gray-500 uppercase">
                            {x.requires_logbook_stage === "none"
                              ? "No Logbook"
                              : x.requires_logbook_stage}
                          </div>
                          {/* The other ways in. Shown as "or" because that is
                              what they are — a candidate needs any one. */}
                          {x.prerequisite_exam_id ? (
                            <div className="text-[10px] font-bold text-indigo-600 uppercase mt-0.5">
                              or pass in{" "}
                              {data.find((e: any) => e.id === x.prerequisite_exam_id)?.code ??
                                "another exam"}
                            </div>
                          ) : null}
                          {x.paid_application_exam_id ? (
                            <div className="text-[10px] font-bold text-indigo-600 uppercase mt-0.5">
                              or paid application for{" "}
                              {data.find(
                                (e: any) => e.id === x.paid_application_exam_id,
                              )?.code ?? "another exam"}
                            </div>
                          ) : null}
                        </td>
                        <td className="px-8 py-5 text-center">
                          <span
                            className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border ${
                              x.is_active
                                ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                                : "bg-gray-50 text-gray-400 border-gray-100"
                            }`}
                          >
                            {x.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-8 py-5 text-right">
                          <div className="flex justify-end gap-1 items-center">
                            <AccessGate perm={PERMS.exam.edit}>
                              <Tooltip text="Modify Template">
                                <button
                                  onClick={() => setEditTarget(x)}
                                  className="p-2.5 text-gray-400 hover:text-blue-600 hover:bg-white rounded-xl border border-transparent hover:border-gray-100 transition-all"
                                >
                                  <Pencil className="w-4 h-4" />
                                </button>
                              </Tooltip>
                              <Tooltip
                                text={x.is_active ? "Deactivate" : "Activate"}
                              >
                                <button
                                  onClick={() =>
                                    handleUpdate(x.id, {
                                      is_active: !x.is_active,
                                    })
                                  }
                                  className="p-2.5 text-gray-400 hover:text-amber-600 hover:bg-white rounded-xl border border-transparent hover:border-gray-100 transition-all"
                                >
                                  {x.is_active ? (
                                    <Lock className="w-4 h-4" />
                                  ) : (
                                    <Unlock className="w-4 h-4" />
                                  )}
                                </button>
                              </Tooltip>
                            </AccessGate>
                            <AccessGate perm={PERMS.exam.delete}>
                              <Tooltip text="Delete Record">
                                <button
                                  onClick={() => handleDelete(x)}
                                  className="p-2.5 text-gray-400 hover:text-rose-600 hover:bg-white rounded-xl border border-transparent hover:border-gray-100 transition-all"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </Tooltip>
                            </AccessGate>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* 5. Edit Modal */}
        {editTarget && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div
              className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
              onClick={() => setEditTarget(null)}
            />
            {/* A column with a capped height: the header and footer hold their
                place and the fields scroll between them. Without the cap the
                dialog grew past the viewport and clipped its own Update button,
                with nothing to scroll. */}
            <div className="relative flex flex-col w-full max-w-lg max-h-[90vh] bg-white rounded-3xl sm:rounded-[2.5rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
              <div className="shrink-0 p-5 sm:p-8 border-b border-gray-50 flex justify-between items-center gap-3 bg-gray-50/30">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="shrink-0 p-2.5 bg-gray-900 rounded-2xl text-white shadow-lg">
                    <Pencil className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-black text-gray-900 text-sm uppercase tracking-tighter leading-none truncate">
                      Modify Exam
                    </h3>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1.5 truncate">
                      Catalog Synchronization
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setEditTarget(null)}
                  className="shrink-0 p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-5 sm:p-8 space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                      Code
                    </label>
                    <input
                      value={editTarget.code}
                      onChange={(e) =>
                        setEditTarget({ ...editTarget, code: e.target.value })
                      }
                      className="w-full border-b-2 border-gray-100 py-2 font-black text-gray-800 outline-none focus:border-indigo-500 bg-transparent"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                      Baseline
                    </label>
                    <select
                      value={editTarget.requires_logbook_stage}
                      onChange={(e) =>
                        setEditTarget({
                          ...editTarget,
                          requires_logbook_stage: e.target.value,
                        })
                      }
                      className="w-full border-b-2 border-gray-100 py-2 text-sm font-bold text-gray-800 bg-transparent outline-none"
                    >
                      <option value="none">None</option>
                      <option value="stage1">Stage 1</option>
                      <option value="stage2">Stage 2</option>
                      <option value="stage3">Stage 3</option>
                    </select>
                  </div>
                </div>

                <AlternateRouteField
                  kind="pass"
                  exams={data}
                  value={editTarget.prerequisite_exam_id}
                  stage={editTarget.requires_logbook_stage}
                  otherRouteSet={!!editTarget.paid_application_exam_id}
                  // An exam cannot be its own prerequisite — the server refuses
                  // it, so it is not offered here either.
                  excludeId={editTarget.id}
                  onChange={(v) => setEditTarget({ ...editTarget, prerequisite_exam_id: v })}
                />

                <AlternateRouteField
                  kind="paid"
                  exams={data}
                  value={editTarget.paid_application_exam_id}
                  stage={editTarget.requires_logbook_stage}
                  otherRouteSet={!!editTarget.prerequisite_exam_id}
                  excludeId={editTarget.id}
                  onChange={(v) =>
                    setEditTarget({ ...editTarget, paid_application_exam_id: v })
                  }
                />
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                    Title
                  </label>
                  <input
                    value={editTarget.title}
                    onChange={(e) =>
                      setEditTarget({ ...editTarget, title: e.target.value })
                    }
                    className="w-full border-b-2 border-gray-100 py-2 font-black text-gray-800 outline-none focus:border-indigo-500 bg-transparent"
                  />
                </div>

                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={!!editTarget.is_interview}
                    onChange={(e) =>
                      setEditTarget({ ...editTarget, is_interview: e.target.checked })
                    }
                    className="shrink-0 w-4 h-4 mt-0.5 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                  />
                  <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest group-hover:text-gray-900 transition-colors">
                    Interview only (no courses)
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={!!editTarget.requires_application_form}
                    onChange={(e) =>
                      setEditTarget({
                        ...editTarget,
                        requires_application_form: e.target.checked,
                      })
                    }
                    className="shrink-0 w-4 h-4 mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest group-hover:text-gray-900 transition-colors">
                    Requires application form
                  </span>
                </label>

                <div className="pt-4 border-t border-gray-100">
                  <EntryRequirementFields
                    values={editTarget}
                    onChange={(patch) => setEditTarget({ ...editTarget, ...patch })}
                  />
                </div>
              </div>

              <div className="shrink-0 p-5 sm:p-8 bg-gray-50 border-t border-gray-100 flex gap-3">
                <button
                  onClick={() => setEditTarget(null)}
                  className="flex-1 py-4 text-[10px] font-black uppercase tracking-widest text-gray-400 hover:text-gray-600 transition-colors"
                >
                  Discard
                </button>
                <button
                  onClick={() => handleUpdate(editTarget.id, editTarget)}
                  disabled={updating}
                  className="flex-[2] bg-gray-900 text-white rounded-2xl py-4 text-[10px] font-black uppercase tracking-[0.2em] shadow-xl shadow-gray-200 hover:bg-indigo-600 transition-all"
                >
                  {updating ? "Syncing..." : "Update Template"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </PageGate>
  );
}
